/*
  Скан выписки из домовой книги в шаге 3 (решение владельца 02.10.2026, макет А).

  До этого дня шаг принимал только таблицу жильцов, а распознавание сканов жило
  во вкладке «Файлы»: умение было, а в шаге, ради которого оно заводилось, его
  не было, и подпись шага прямо говорила «сканом загружать не нужно».

  🔴 Второго распознавания здесь нет и быть не должно. Скан уходит той же
  дверью, что на вкладке «Файлы» (`POST /uploads`), сверку показывает тот же
  `ScanReview`, перенос - тот же `TransferPanel`. Шаг отличается от вкладки
  ровно одним: он показывает не все присланные файлы, а свои.

  Чем таблица отличается от скана для человека: таблица ЗАМЕНЯЕТ состав счёта
  целиком, а сверенный скан ДОПИСЫВАЕТ людей в домовую книгу
  (`composition_repository.add_manual_residents`). Поэтому подпись под кнопками
  шага меняется по виду выбранного файла.

  06.10.2026 (макет Б владельца): своей кнопки отправки здесь больше нет - скан
  уходит общей кнопкой шага (`ResidentsUpload`), а сюда приходят номера
  присланного (`sent`). И главное - какие сканы показывать, решает СЕРВЕР
  (`ownFiles`): владелец прислал выписку, закрыл страницу и вернулся к пустому
  шагу, потому что «свои» файлы шаг помнил только в памяти страницы.
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { dateTime } from '../api/format.js'
import { Notice } from './Common.jsx'
import ScanReview from './files/ScanReview.jsx'
import TransferPanel from './files/TransferPanel.jsx'
import useOwnScans from './files/useOwnScans.js'
import useUploads from './files/useUploads.js'

/* Подпись у кнопки переноса. Она здесь не из вежливости: кнопка одна на весь
   накопитель (§1.3.1) и забирает в портфель ВСЁ собранное, включая файлы,
   присланные во вкладке «Файлы». Человек, пришедший сюда за одной выпиской,
   обязан знать это до нажатия, а не из отчёта после. */
export const TRANSFER_NOTE =
  'Перенос заберёт в портфель всё готовое из накопителя, а не только эту выписку: ' +
  'там же лежат сведения из файлов, присланных во вкладке «Файлы».'

const SCAN_SUFFIXES = ['.pdf', '.jpg', '.jpeg', '.png']

/* Вид файла определяется по имени: это выбор ЭКРАНА - какие кнопки показать.
   Скан ли это на самом деле, решает сервер (`documents.pdf_is_scan`: PDF с
   текстовым слоем - не скан), и его решение видно в раскладке листа. */
export function isScan(file) {
  const name = file?.name?.toLowerCase() ?? ''
  return SCAN_SUFFIXES.some((suffix) => name.endsWith(suffix))
}

/* Четыре секунды на страницу - замер 06.10.2026 на 18 настоящих страницах,
   когда бланк стал читаться по графам (было две при чтении целиком). */
const STATE_NOTES = {
  received:
    'Распознаём страницы - около четырёх секунд на страницу. Страницу можно закрыть: скан ' +
    'останется здесь.',
  read: 'Прочитано. Сведения лежат в накопителе и ждут переноса в портфель.',
}

/* Скан ли это, решает СЕРВЕР, а не имя файла: PDF с текстовым слоем он читает
   таблицей, и сверять в нём нечего. Поэтому кнопка сверки цепляется за
   раскладку листа, а не за расширение, с которым файл прислали. */
const isScanFile = (file) => file.sheets?.[0]?.layout === 'scan'

function ScanRow({ file, onReview, locked }) {
  const reviewable = isScanFile(file) && (file.state === 'needs_review' || file.state === 'read')
  return (
    <div className="row row--between">
      {/* Без `onec__summary`: у него своя верхняя граница - разделитель под
          разбором файла, и здесь она читалась обрезанной чертой (осмотр
          02.10.2026). Имя файла выделено, состояние рядом серым. Время
          присылки - чтобы, вернувшись, человек узнал свой файл. */}
      <p className="filename">
        <b>{file.filename}</b>
        {file.state === 'failed' ? null : (
          <span className="muted">
            {' '}
            - прислан {dateTime(file.uploaded_at)}. {STATE_NOTES[file.state] ?? 'Ждёт вашей сверки.'}
          </span>
        )}
      </p>
      {reviewable ? (
        <button
          type="button"
          className={file.state === 'needs_review' ? 'button' : 'button button--quiet'}
          disabled={locked}
          onClick={() => onReview(file.id)}
        >
          {file.state === 'needs_review' ? 'Сверить' : 'Открыть сверку'}
        </button>
      ) : null}
    </div>
  )
}

/*
  Какие файлы накопителя - «свои» для шага (отбирает сервер, `useOwnScans`).

  🔴 Скан, который читается, ждёт сверки или не прочитался, показывается
  ВСЕГДА - по состоянию с сервера (06.10.2026). Человек мог закрыть страницу,
  пока шло распознавание; до этого дня шаг помнил присланное только в памяти
  страницы, и читающийся файл после возвращения пропадал. Скан ли это, тоже
  знает сервер: опись файла составлена при приёме, до чтения.

  Память страницы осталась для двух случаев. Присланное отсюда видно и после
  того, как сервер прочитал PDF таблицей (сверять нечего, но «Прочитано» человек
  должен увидеть). И открытый на сверку файл остаётся своим ПОСЛЕ неё (осмотр
  02.10.2026): иначе человек подтверждал последнюю страницу, файл переставал
  ждать сверки - и блок исчезал вместе с кнопкой переноса. С 21.10.2026 память
  страницы уходит на сервер номерами файлов (`ids`), а не отбирается в браузере
  по списку: список файлов теперь одна страница.
*/
const remember = (previous, ids) => [...new Set([...previous, ...ids])]

export default function ResidentsScanWork({ sent }) {
  const { data, stamp, error, summary, transferring, reload } = useUploads()
  const [ownIds, setOwnIds] = useState([])
  const own = useOwnScans(data ? stamp : null, ownIds)
  const [work, setWork] = useState({ busy: null, error: null, place: null })
  const [reviewing, setReviewing] = useState(null)

  // Скан прислан общей кнопкой шага - он свой, и список перечитывается сразу:
  // иначе человек увидел бы его только со следующим опросом.
  useEffect(() => {
    if (sent.length === 0) return
    setOwnIds((previous) => remember(previous, sent))
    reload()
  }, [sent, reload])

  const review = (id) => {
    setOwnIds((previous) => remember(previous, [id]))
    setReviewing(id)
  }

  const act = async (place, action) => {
    setWork({ busy: place, error: null, place })
    try {
      await action()
      await reload()
      setWork({ busy: null, error: null, place: null })
    } catch (failure) {
      setWork({ busy: null, error: failure, place })
    }
  }

  const errorAt = (place) => (work.place === place ? work.error : null)
  const mine = own.files
  const reviewed = mine.find((file) => file.id === reviewing)

  return (
    <>
      {/* Сбой опроса показывается только когда есть о чём: пустой накопитель и
          молчащий сервер человека шага 3 не касаются. */}
      {mine.length > 0 && (error ?? own.error) ? <Notice error={error ?? own.error} /> : null}

      {mine.length > 0 ? (
        <section className="section card">
          {mine.map((file) => (
            <div key={file.id} className="stack">
              <ScanRow file={file} locked={transferring} onReview={review} />
              {file.state === 'failed' ? (
                <Notice kind="error">
                  <div className="notice__what filename">«{file.filename}» прочитать не удалось.</div>
                  <div className="notice__do">{file.error_message}</div>
                </Notice>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      {reviewed ? (
        <ScanReview
          key={reviewed.id}
          file={reviewed}
          locked={transferring}
          onDecided={reload}
          onClose={() => setReviewing(null)}
        />
      ) : null}

      {/* Сводка приходит своим запросом (02.10.2026): панель встаёт сразу с
          ходом переноса, а числа и кнопку дорисовывает, когда сводка пришла. */}
      {mine.length > 0 && data ? (
        <TransferPanel
          summary={summary}
          transfer={data.last_transfer}
          busy={work.busy === 'transfer'}
          error={errorAt('transfer')}
          note={TRANSFER_NOTE}
          onTransfer={(force, only) => act('transfer', () => api.transferUploads(force, only))}
        />
      ) : null}
    </>
  )
}
